import { inject, injectable, multiInject } from 'inversify'
import { makeObservable, computed, reaction } from 'mobx'
import { FeedbackRepository } from '@core/feedback/feedback.repository'
import { AuthenticationRepository } from '@core/infra/authentication/authentication.repository'
import { NotificationRepository } from '@core/infra/notifications/notification.repository'
import { Types } from '@core/ioc/types'
import type { AppLifecycle } from '@core/interfaces/app-lifecycle'
import { Router } from '@core/routing/router'
import { I18nRepository } from '@core/infra/i18n/i18n.repository'
import { NavigationRepository } from '@core/infra/navigation/navigation.repository'
import type { LoggingGateway } from '@core/services/logging.gateway'

@injectable()
export class AppPresenter {
  @inject(Router)
  router!: Router

  @inject(FeedbackRepository)
  messagesRepository!: FeedbackRepository

  @inject(AuthenticationRepository)
  authenticationRepository!: AuthenticationRepository

  @inject(NotificationRepository)
  notificationRepository!: NotificationRepository

  @inject(I18nRepository)
  i18nRepository!: I18nRepository

  @inject(NavigationRepository)
  navigationRepository!: NavigationRepository

  @inject(Types.ILoggingGateway)
  loggingGateway!: LoggingGateway

  /**
   * Everything with a one-time startup step, resolved as a group. This used to
   * be five named injections and five ordered calls below, so adding
   * infrastructure meant editing this file. See draft §O (Composition Root).
   */
  @multiInject(Types.IAppLifecycle)
  lifecycleParticipants!: AppLifecycle[]

  private watchesSessionExpiry = false

  get currentRoute() {
    return this.router.currentRoute
  }

  // Pre-formatted for RouteInfoComponent's debug readout — serialising in the
  // component would be a transformation in the view (machine-architecture.md §3.4).
  get routeInfoViewModel() {
    const { routeId, routeDef, params, query } = this.router.currentRoute

    return {
      routeId: routeId ?? '',
      pathLabel: this.i18nRepository.t('routeInfo.path'),
      paramsLabel: this.i18nRepository.t('routeInfo.params'),
      queryLabel: this.i18nRepository.t('routeInfo.query'),
      pathText: routeDef?.path ?? '',
      paramsText: JSON.stringify(params),
      queryText: JSON.stringify(query),
    }
  }

  get loadingLabel(): string {
    return this.i18nRepository.t('common.loading')
  }

  // A route's chunk failed to fetch (offline navigation, a stale chunk after
  // a deploy) — PageBoundaryComponent's error, not a genuinely unexpected
  // render bug, so it stays out of ErrorBoundaryComponent's tier-3 crash
  // screen and gets its own in-page recovery instead (message + retry,
  // shell and nav left standing).
  get pageLoadFailureVm() {
    return {
      message: this.i18nRepository.t('app.pageLoadFailed'),
      retryLabel: this.i18nRepository.t('common.retry'),
    }
  }

  reportPageLoadFailure = (error: Error): void => {
    this.loggingGateway.error('Page chunk failed to load', { message: error.message })
  }

  // Global route-transition indicator — true for the span of any navigation
  // (goToId called through commit/abort), regardless of which page it's on.
  get isLoading() {
    return this.router.isLoading
  }

  constructor() {
    makeObservable(this, {
      currentRoute: computed,
      routeInfoViewModel: computed,
      isLoading: computed,
    })
  }

  load = (onRouteChange: () => void) => {
    // Written out rather than registered: this has a real ordering constraint —
    // the session must be restored before routes resolve, so the auth guard
    // sees the token on the very first navigation. The registry below is
    // order-independent by contract.
    this.authenticationRepository.restoreSession()

    this.lifecycleParticipants.forEach((participant) => participant.initialize())
    this.watchForSessionExpiry()

    const onRouteChangeWrapper = () => {
      // Clears only the scopes configured to clear on navigation, in one
      // action — this runs from a plain router callback, so the write needs
      // its own action wrapper.
      this.messagesRepository.resetForRouteChange()
      // Browsing the menu away from the current route is a temporary detour;
      // once the user actually goes somewhere, the menu follows again.
      this.navigationRepository.resetBrowseAnchor()
      onRouteChange()
    }
    this.router.registerRoutes(onRouteChangeWrapper)
  }

  /**
   * A token refresh can fail at any moment, including while the user is sitting
   * on a secure page doing nothing. The Repository can only tear the session
   * down (§3.2 — it must not reach a Presenter or the Router), so it raises a
   * flag and the response to it lives here.
   */
  private watchForSessionExpiry = () => {
    if (this.watchesSessionExpiry) return

    reaction(
      () => this.authenticationRepository.sessionExpired,
      (sessionExpired) => {
        if (sessionExpired) this.handleSessionExpiry()
      },
    )
    this.watchesSessionExpiry = true
  }

  private handleSessionExpiry = async () => {
    // Cleared first: the flag is an event, and leaving it set would re-fire
    // this on the next unrelated change.
    this.authenticationRepository.clearSessionExpired()

    // Not the feedback list — that is wiped on every route change, and this
    // message has to survive the redirect to the login page.
    this.notificationRepository.notify('warning', this.i18nRepository.t('app.sessionExpired'))

    // Re-enter the current route rather than jumping straight to Login: the
    // auth guard then sees the missing token, redirects, and records where to
    // return to after logging back in (Router.redirectTarget).
    const { routeId, params, query } = this.router.currentRoute
    if (routeId) await this.router.goToId(routeId, params, query)
  }
}
